import { useState } from "react";
import { toast } from "sonner";
import { Pill, ArrowLeft } from "lucide-react";
import { api, PROFILE_KEY } from "@/lib/api";
import { Input } from "@/components/ui/input";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ") || "Something went wrong";
  return "Something went wrong";
};

const Field = (props) => (
  <Input
    {...props}
    className="h-13 rounded-xl border-slate-800 bg-slate-900/70 px-4 py-3.5 text-[16px] placeholder:text-slate-600"
  />
);

const Primary = ({ children, ...rest }) => (
  <button
    {...rest}
    className="h-14 w-full rounded-full bg-gradient-to-r from-sky-500 to-blue-600 text-[17px] font-semibold text-white shadow-[0_0_30px_rgba(56,189,248,0.3)] transition-transform duration-150 active:scale-95 disabled:opacity-50"
  >
    {children}
  </button>
);

export default function SignIn() {
  const [mode, setMode] = useState("start"); // start | password | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetLink, setResetLink] = useState("");

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/day";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (path, body) => {
    setBusy(true);
    try {
      await api.post(path, body);
      localStorage.removeItem(PROFILE_KEY);
      window.location.href = "/day";
    } catch (e) {
      toast.error(errText(e));
      setBusy(false);
    }
  };

  const forgot = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      if (data.reset_link) {
        setResetLink(data.reset_link);
        toast.message("Email sending isn't configured yet — use the link below");
      } else {
        toast.success("Check your inbox for the reset link");
      }
    } catch (e) {
      toast.error(errText(e));
    }
    setBusy(false);
  };

  return (
    <div className="grain relative min-h-screen bg-[#0B1120] text-slate-100">
      <div className="aurora" />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col justify-center px-7 py-10">
        <div className="rise mb-10 flex flex-col items-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-sky-500/30 bg-sky-500/10 shadow-[0_0_30px_rgba(56,189,248,0.25)]">
            <Pill className="h-7 w-7 text-sky-300" />
          </div>
          <h1 className="mt-5 text-4xl font-extrabold tracking-tight">Pillbox</h1>
        </div>

        {mode === "start" && (
          <div className="rise space-y-3">
            <Primary data-testid="google-signin-btn" onClick={google}>
              Continue with Google
            </Primary>
            <button
              data-testid="email-signin-btn"
              onClick={() => setMode("password")}
              className="h-14 w-full rounded-full border border-slate-700 text-[17px] font-semibold text-slate-200 transition-colors duration-200 hover:bg-slate-900 active:scale-95"
            >
              Sign in with email
            </button>
            <button
              data-testid="go-register-btn"
              onClick={() => setMode("register")}
              className="w-full pt-2 text-center text-[15px] text-slate-500"
            >
              New here? Create an account
            </button>
          </div>
        )}

        {mode !== "start" && (
          <div className="rise space-y-3">
            <button
              data-testid="auth-back-btn"
              onClick={() => {
                setMode("start");
                setResetLink("");
              }}
              className="mb-1 flex items-center gap-1.5 text-[15px] text-slate-500"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>

            {mode === "register" && (
              <Field
                data-testid="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            )}

            <Field
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
            />

            {mode !== "forgot" && (
              <Field
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
              />
            )}

            {mode === "password" && (
              <>
                <Primary
                  data-testid="login-submit-btn"
                  disabled={busy}
                  onClick={() => submit("/auth/login", { email, password })}
                >
                  Sign in
                </Primary>
                <button
                  data-testid="forgot-password-btn"
                  onClick={() => setMode("forgot")}
                  className="w-full pt-1 text-center text-[15px] text-slate-500"
                >
                  Forgot password?
                </button>
              </>
            )}

            {mode === "register" && (
              <Primary
                data-testid="register-submit-btn"
                disabled={busy}
                onClick={() => submit("/auth/register", { email, password, name })}
              >
                Create account
              </Primary>
            )}

            {mode === "forgot" && (
              <>
                <Primary data-testid="forgot-submit-btn" disabled={busy} onClick={forgot}>
                  Send reset link
                </Primary>
                {resetLink && (
                  <a
                    data-testid="reset-link-inline"
                    href={resetLink}
                    className="block break-all rounded-xl border border-sky-500/30 bg-sky-500/10 p-3 text-[13px] text-sky-200"
                  >
                    {resetLink}
                  </a>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
